import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import QuantityStepper from "@/components/base/QuantityStepper";
import SizeGuideModal from "./components/SizeGuideModal";
import RelatedProducts from "./components/RelatedProducts";
import { useProducts } from "@/lib/siteContent";
import { formatBRL, installmentText } from "@/lib/format";
import { useStore } from "@/store/StoreContext";

export default function ProductPage() {
  const { id } = useParams();
  const products = useProducts();
  const product = useMemo(() => (id ? products.find((item) => item.id === id) : undefined), [id, products]);
  const { addToCart, isFavorite, toggleFavorite } = useStore();

  const [colorName, setColorName] = useState("");
  const [size, setSize] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [sizeError, setSizeError] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    if (product) {
      setColorName(product.colors[0].name);
      setSize("");
      setQuantity(1);
      setSizeError(false);
    }
  }, [product]);

  if (!product) {
    return (
      <section className="w-full px-4 py-20 md:px-8 md:py-28">
        <div className="mx-auto flex max-w-xl flex-col items-center text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-600">
            <i className="ri-shopping-bag-3-line text-3xl" />
          </span>
          <h1 className="mt-5 font-heading text-3xl text-foreground-900">
            Não encontramos este produto
          </h1>
          <p className="mt-3 text-sm text-foreground-600">
            Ela pode ter saído da vitrine ou o endereço está incorreto. Dê uma olhada nas novidades.
          </p>
          <Link
            to="/novidades"
            className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            Ver novidades
          </Link>
        </div>
      </section>
    );
  }

  const selectedColor = product.colors.find((color) => color.name === colorName) ?? product.colors[0];
  const price = product.promoPrice ?? product.price;
  const hasPromo = typeof product.promoPrice === "number";
  const favorite = isFavorite(product.id);
  const available = product.stock > 0;

  const handleAdd = () => {
    if (!size) {
      setSizeError(true);
      return;
    }
    setSizeError(false);
    addToCart(product, selectedColor.name, size, quantity);
  };

  return (
    <div className="w-full">
      <div className="mx-auto max-w-7xl px-4 pt-8 md:px-8">
        <nav aria-label="Caminho de navegação" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
          <Link to="/" className="cursor-pointer whitespace-nowrap transition-colors hover:text-primary-600">
            Início
          </Link>
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-arrow-right-s-line" />
          </span>
          <Link
            to={`/categoria/${product.categoryId}`}
            className="cursor-pointer whitespace-nowrap transition-colors hover:text-primary-600"
          >
            {product.category}
          </Link>
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-arrow-right-s-line" />
          </span>
          <span className="whitespace-nowrap text-foreground-800">{product.name}</span>
        </nav>
      </div>

      <section className="w-full bg-background-50">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-12">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
            {/* Galeria */}
            <div>
              <div className="relative w-full overflow-hidden rounded-lg border border-background-200 bg-background-100">
                <div className="aspect-[4/5] w-full">
                  <img
                    src={selectedColor.image}
                    alt={`${product.name} na cor ${selectedColor.name} — Auréa`}
                    title={`${product.name} — ${selectedColor.name} Auréa`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                {product.tag ? (
                  <span
                    className={`absolute left-4 top-4 rounded-full px-3 py-1 text-[10px] font-medium uppercase tracking-[0.14em] ${
                      product.tag === "Novo"
                        ? "bg-secondary-500 text-foreground-950"
                        : product.tag === "Mais vendido"
                          ? "bg-primary-600 text-background-50"
                          : "bg-accent-500 text-background-50"
                    }`}
                  >
                    {product.tag}
                  </span>
                ) : null}
              </div>

              <div className="mt-4 flex flex-wrap gap-3">
                {product.colors.map((color) => {
                  const isActive = color.name === selectedColor.name;
                  return (
                    <button
                      key={color.name}
                      type="button"
                      onClick={() => setColorName(color.name)}
                      aria-label={`Ver ${product.name} na cor ${color.name}`}
                      aria-pressed={isActive}
                      className={`h-20 w-16 cursor-pointer overflow-hidden rounded-md border-2 transition-all ${
                        isActive ? "border-primary-500" : "border-background-300 hover:border-primary-300"
                      }`}
                    >
                      <img
                        src={color.image}
                        alt={`${product.name} na cor ${color.name}`}
                        className="h-full w-full object-cover object-top"
                      />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Informações */}
            <div>
              <span className="text-[11px] uppercase tracking-[0.2em] text-secondary-700">
                {product.category}
              </span>
              <div className="mt-2 flex items-start justify-between gap-4">
                <h1 className="font-heading text-3xl font-medium leading-tight text-foreground-900 md:text-4xl">
                  {product.name}
                </h1>
                <button
                  type="button"
                  onClick={() => toggleFavorite(product)}
                  aria-label={favorite ? "Remover dos favoritos" : "Adicionar aos favoritos"}
                  aria-pressed={favorite}
                  className={`flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full border transition-colors ${
                    favorite
                      ? "border-primary-500 bg-primary-500 text-background-50"
                      : "border-background-300 text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  <i className={favorite ? "ri-heart-3-fill text-xl" : "ri-heart-3-line text-xl"} />
                </button>
              </div>
              <p className="mt-1 text-xs tracking-[0.1em] text-foreground-400">
                Código {product.code}
              </p>

              <div className="mt-5 flex flex-wrap items-baseline gap-3">
                <span className="font-heading text-3xl text-primary-700">{formatBRL(price)}</span>
                {hasPromo ? (
                  <span className="text-base text-foreground-400 line-through">
                    {formatBRL(product.price)}
                  </span>
                ) : null}
              </div>
              <p className="mt-1 text-sm text-foreground-500">
                ou {installmentText(price, product.installments)}
              </p>

              {/* Cores */}
              <div className="mt-7">
                <p className="text-sm text-foreground-800">
                  Escolha a cor que combina com você.
                </p>
                <p className="mt-1 text-xs text-foreground-500">
                  Cor selecionada:{" "}
                  <strong className="font-medium text-foreground-800">{selectedColor.name}</strong>
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {product.colors.map((color) => {
                    const isActive = color.name === selectedColor.name;
                    return (
                      <button
                        key={color.name}
                        type="button"
                        onClick={() => setColorName(color.name)}
                        aria-pressed={isActive}
                        className={`flex cursor-pointer items-center gap-2 rounded-full border px-3 py-2 text-sm transition-colors ${
                          isActive
                            ? "border-primary-500 bg-primary-50 text-foreground-900"
                            : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                        }`}
                      >
                        <span
                          className="h-5 w-5 rounded-full border border-background-300"
                          style={{ backgroundColor: color.hex }}
                        />
                        {color.name}
                        {isActive ? (
                          <span className="flex h-4 w-4 items-center justify-center">
                            <i className="ri-check-line text-primary-600" />
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tamanhos */}
              <div className="mt-7">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-foreground-800">Agora selecione o seu tamanho.</p>
                  <button
                    type="button"
                    onClick={() => setGuideOpen(true)}
                    className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-xs text-primary-700 underline underline-offset-4 transition-colors hover:text-primary-600"
                  >
                    <i className="ri-ruler-2-line text-sm" />
                    Ver guia de medidas
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {product.sizes.map((item) => {
                    const out = product.sizesOut?.includes(item) ?? false;
                    const isActive = size === item;
                    return (
                      <button
                        key={item}
                        type="button"
                        disabled={out}
                        aria-pressed={isActive}
                        onClick={() => {
                          setSize(item);
                          setSizeError(false);
                        }}
                        className={`min-w-[3rem] rounded-md border px-4 py-2.5 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${
                          out
                            ? "cursor-not-allowed border-background-200 bg-background-100 text-foreground-300 line-through"
                            : isActive
                              ? "cursor-pointer border-primary-500 bg-primary-500 text-background-50"
                              : "cursor-pointer border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                        }`}
                      >
                        {item}
                      </button>
                    );
                  })}
                </div>
                {sizeError ? (
                  <p className="mt-3 flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-xs text-accent-900">
                    <i className="ri-error-warning-line text-base" />
                    Escolha um tamanho para continuar.
                  </p>
                ) : null}
              </div>

              {/* Quantidade */}
              <div className="mt-7">
                <p className="text-sm text-foreground-800">Quantidade</p>
                <div className="mt-3 flex flex-wrap items-center gap-4">
                  <QuantityStepper
                    value={quantity}
                    max={product.stock || 99}
                    onChange={setQuantity}
                  />
                  <span className="text-xs text-foreground-500">
                    {available
                      ? product.stock <= 3
                        ? `Últimos ${product.stock} itens`
                        : `Disponível — ${product.stock} itens em estoque`
                      : "No momento indisponível"}
                  </span>
                </div>
              </div>

              {/* Ações */}
              <div className="mt-7 flex flex-col gap-3">
                <button
                  type="button"
                  onClick={handleAdd}
                  className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-shopping-bag-3-line text-lg" />
                  Adicionar à sacola
                </button>
                <Link
                  to="/sacola"
                  className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-6 py-3.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-50"
                >
                  <i className="ri-shopping-bag-3-line text-lg" />
                  Ir para a sacola
                </Link>
              </div>
              <p className="mt-3 text-xs text-foreground-500">
                A disponibilidade e a entrega serão confirmadas no atendimento.
              </p>

              {/* Descrição */}
              <div className="mt-8 border-t border-background-200 pt-7">
                <h2 className="font-heading text-xl text-foreground-900">Sobre o produto</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  {product.description}
                </p>

                <dl className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="rounded-lg border border-background-200 bg-background-100 p-4">
                    <dt className="text-[11px] uppercase tracking-[0.14em] text-foreground-500">
                      Material
                    </dt>
                    <dd className="mt-1 text-sm text-foreground-800">{product.fabric}</dd>
                  </div>
                  <div className="rounded-lg border border-background-200 bg-background-100 p-4">
                    <dt className="text-[11px] uppercase tracking-[0.14em] text-foreground-500">
                      Ajuste
                    </dt>
                    <dd className="mt-1 text-sm text-foreground-800">{product.fit}</dd>
                  </div>
                  <div className="rounded-lg border border-background-200 bg-background-100 p-4">
                    <dt className="text-[11px] uppercase tracking-[0.14em] text-foreground-500">
                      Cuidados
                    </dt>
                    <dd className="mt-1 text-sm text-foreground-800">{product.care}</dd>
                  </div>
                </dl>
              </div>

              <div className="mt-7">
                <Link
                  to={`/categoria/${product.categoryId}`}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-600"
                >
                  <i className="ri-arrow-left-line text-base" />
                  Voltar para {product.category}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <RelatedProducts current={product} />

      <SizeGuideModal
        open={guideOpen}
        group={product.sizeGroup}
        onClose={() => setGuideOpen(false)}
      />
    </div>
  );
}